// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { useSafeCartDrawer } from '@iheartjane/cart';
import { Flex, useMobileMediaQuery } from '@iheartjane/reefer';
import type { AppMode, Breadcrumb, PriceId } from '@iheartjane/types';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { Link, useParams } from 'react-router';

import type { AlgoliaProduct, JaneSearchState } from '@jane/search/data-access';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, Product } from '@jane/shared/models';
import type {
  Brand,
  EcommPaths,
  MenuProduct as ZMenuProduct,
} from '@jane/shared/types';
import type { Categories } from '@jane/shared/util';
import { monolithPaths, postMessageToIframeParent } from '@jane/shared/util';

export const MENU_PRODUCT_ITEM_HEIGHT = 475;
export const MENU_PRODUCT_MOBILE_ITEM_HEIGHT = 425;
export const MENU_PRODUCT_SPONSORED_HEIGHT = 32;

export const DEFAULT_MOBILE_CAROUSEL_CARD_WIDTH = 165;

export type ProductCardMode = 'default' | 'storeMenu';

export const PRICE_LOOKUP: Record<string, string> = {
  HALF_GRAM: '.5g',
  GRAM: '1g',
  TWO_GRAM: '2g',
  EIGHTH_OUNCE: '3.5g',
  QUARTER_OUNCE: '7g',
  HALF_OUNCE: '14g',
  OUNCE: '28g',
};

export const ItemContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  position: 'relative',
  transition: 'box-shadow .3s',
  ':focus-visible': {
    boxShadow: '0 2px 11px 0 rgba(108, 108, 108, 0.5)',
    outline: 'none',
  },
});

interface StyledElementType {
  appMode: AppMode;
  baseCardHeight: number;
  height?: string | number;
}

const StyledLink = styled(Link, {
  shouldForwardProp: (propName: string) =>
    propName !== 'appMode' && propName !== 'baseCardHeight',
})<StyledElementType>(({ height, appMode, baseCardHeight, theme }) => ({
  position: 'relative',
  width: '100%',
  height,
  maxHeight: baseCardHeight,
  pointerEvents: appMode === 'headless' ? 'none' : undefined,
  ':focus-visible': {
    outline: 'none',
  },
  ':focus-visible:before': {
    backgroundColor: theme.colors.grays.hover,
    borderRadius: 'inherit',
    content: '""',
    height: '100%',
    position: 'absolute',
    width: '100%',
    zIndex: '1',
  },
}));

const StyledFlex = styled(Flex, {
  shouldForwardProp: (propName: string) =>
    propName !== 'appMode' && propName !== 'baseCardHeight',
})<StyledElementType>(({ height, appMode, baseCardHeight, theme }) => ({
  position: 'relative',
  width: '100%',
  height,
  maxHeight: baseCardHeight,
  cursor: 'pointer',
  pointerEvents: appMode === 'headless' ? 'none' : undefined,
  ':focus-visible': {
    outline: 'none',
  },
  ':focus-visible:before': {
    backgroundColor: theme.colors.grays.hover,
    borderRadius: 'inherit',
    content: '""',
    height: '100%',
    position: 'absolute',
    width: '100%',
    zIndex: '1',
  },
}));

/**
 * Everything `productDetailLink` reads off a product. Structural so any menu,
 * search or cart product satisfies it; `product_id` is optional because only
 * menu products carry it (the `id` fallback below covers the rest).
 */
export interface ProductDetailLinkProduct {
  brand?: string | null;
  id: string | number;
  name?: string;
  product_id?: string | number;
  url_slug?: string | null;
}

export const productDetailLink = ({
  appMode,
  brand,
  bundleSpecialId,
  fromAllSpecials,
  fromSpecialId,
  product,
  store,
}: {
  appMode: AppMode;
  brand?: Pick<Brand, 'id' | 'name'>;
  bundleSpecialId?: string | number | null;
  fromAllSpecials?: boolean | null;
  fromSpecialId?: string | number | null;
  product: ProductDetailLinkProduct;
  store?: { id: number | string; name: string };
}) => {
  const slug = product.url_slug ?? undefined;
  const productNameAndSlugAndId = {
    id: product.product_id || product.id,
    name: product.name,
    slug,
    brand: product.brand,
  };

  if (appMode === 'default') {
    if (store) {
      return monolithPaths.storeProduct({
        store,
        product: productNameAndSlugAndId,
        fromAllSpecials,
        bundleSpecialId,
        fromSpecialId,
      });
    }
    if (brand) {
      return monolithPaths.brandProduct({
        brand,
        product: productNameAndSlugAndId,
        bundleSpecialId,
      });
    }
    return monolithPaths.product({
      product: productNameAndSlugAndId,
      slug,
      bundleSpecialId,
    });
  }

  if (['bloom', 'headless'].includes(appMode)) {
    return monolithPaths.headlessProductDetail(productNameAndSlugAndId);
  }

  return monolithPaths.embeddedProductDetail({
    product: productNameAndSlugAndId,
    fromAllSpecials,
    bundleSpecialId,
    fromSpecialId,
  });
};

export interface LinkProps {
  appMode: AppMode;
  'aria-hidden'?: boolean;
  'aria-label'?: string;
  brand?: Brand;
  breadcrumbs?: Breadcrumb[];
  bundleSpecialId?: string | number | null;
  cartIsOpen: boolean;
  children: ReactNode;
  defaultWeight?: PriceId;
  fromAllSpecials?: boolean | null;
  fromSpecialId?: string | number | null;
  height?: number | string;
  pdpPath?: EcommPaths['menuProduct'];
  product: Product;
  searchState: JaneSearchState<AlgoliaProduct> | undefined;
  store?: { id: number | string; name: string };
  tabIndex?: number;
  trackListViewClick?: () => void;
  trackProductListClick?: () => void;
}

export const ProductDetailPageLink = ({
  appMode,
  'aria-hidden': ariaHidden,
  'aria-label': ariaLabel,
  brand,
  breadcrumbs,
  bundleSpecialId,
  cartIsOpen,
  children,
  defaultWeight,
  fromAllSpecials,
  fromSpecialId,
  height,
  pdpPath,
  product,
  searchState,
  store,
  tabIndex,
  trackListViewClick,
  trackProductListClick,
}: LinkProps) => {
  const { brand_id, brand_name, category, special_id } = useParams<{
    brand_id: string;
    brand_name?: string;
    category?: Categories;
    special_id?: string;
  }>();

  // Optional: product cards also render in trees with no cart drawer at all
  // (kiosk's quick-checkout, business-admin), where this stays inert.
  const closeCartDrawer = useSafeCartDrawer()?.close;

  const memoizedOnClick = useCallback(() => {
    if (cartIsOpen) {
      closeCartDrawer?.();
    }
    trackListViewClick && trackListViewClick();
    trackProductListClick && trackProductListClick();
  }, [searchState, cartIsOpen, closeCartDrawer]);

  const isMobile = useMobileMediaQuery({});

  const baseCardHeight = isMobile
    ? MENU_PRODUCT_MOBILE_ITEM_HEIGHT
    : MENU_PRODUCT_ITEM_HEIGHT;

  const isFromProductBrand = brand_id !== undefined;
  const pdpLink = pdpPath
    ? pdpPath(product, defaultWeight)
    : productDetailLink({
        appMode,
        brand: isFromProductBrand ? brand : undefined,
        bundleSpecialId,
        fromAllSpecials,
        fromSpecialId,
        product,
        store,
      });

  const locationState = {
    ...(searchState ? { searchState } : {}),
    ...(category ? { fromCategory: category } : {}),
    ...(breadcrumbs ? { breadcrumbs } : {}),
    ...(special_id && brand_name && brand_id
      ? { fromBrandSpecialId: special_id }
      : {}),
  };

  if (['bloom', 'headless'].includes(appMode)) {
    return (
      <StyledFlex
        appMode={appMode}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel}
        baseCardHeight={baseCardHeight}
        height={height}
        onClick={() => {
          postMessageToIframeParent({
            messageType: 'navigationEvent',
            payload: {
              name: 'navigateToProduct',
              link: pdpLink,
            },
          });
        }}
        role={ariaHidden ? undefined : 'link'}
      >
        {children}
      </StyledFlex>
    );
  }

  return (
    <StyledLink
      appMode={appMode}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      baseCardHeight={baseCardHeight}
      height={height}
      tabIndex={tabIndex}
      to={pdpLink}
      onClick={memoizedOnClick}
      role="link"
      state={locationState}
    >
      {children}
    </StyledLink>
  );
};

// Generic over the element type, and structural in what it requires, so the v1
// and the v3 cart product both sort without a cast at the call site.
export const sortByPriceId = <T extends { price_id: PriceId }>(
  cartProduct: T[],
  menuProduct: MenuProduct | ZMenuProduct
): T[] =>
  cartProduct.sort((a, b) =>
    (menuProduct?.available_weights || []).indexOf(
      a.price_id.replace('_', ' ')
    ) >
    (menuProduct?.available_weights || []).indexOf(b.price_id.replace('_', ' '))
      ? 1
      : -1
  );
